import type { FormFieldTypeId } from '@/types/form-definition';
import {
  Calendar,
  CalendarClock,
  CheckSquare,
  CircleDot,
  CreditCard,
  GripVertical,
  Hash,
  Heading,
  ListChecks,
  Mail,
  Minus,
  Phone,
  Pilcrow,
  FileText,
  Send,
  TextCursorInput,
  Type,
  User,
  MapPin,
  PenLine,
  Paperclip,
  Clock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type PaletteItem = {
  type: FormFieldTypeId;
  label: string;
  icon: LucideIcon;
  description?: string;
};

export type PaletteGroup = { id: string; title: string; items: PaletteItem[] };

export const PALETTE_GROUPS: PaletteGroup[] = [
  {
    id: 'basic',
    title: 'Basic',
    items: [
      { type: 'shortText', label: 'Short Text', icon: Type, description: 'Single line input' },
      { type: 'longText', label: 'Long Text', icon: TextCursorInput, description: 'Paragraph' },
      { type: 'email', label: 'Email', icon: Mail },
      { type: 'phone', label: 'Phone', icon: Phone },
      { type: 'number', label: 'Number', icon: Hash },
      { type: 'fullName', label: 'Full name', icon: User },
      { type: 'address', label: 'Address', icon: MapPin },
    ],
  },
  {
    id: 'choice',
    title: 'Choice',
    items: [
      { type: 'dropdown', label: 'Dropdown', icon: ListChecks },
      { type: 'singleChoice', label: 'Single choice', icon: CircleDot },
      { type: 'multipleChoice', label: 'Multiple choice', icon: CheckSquare },
    ],
  },
  {
    id: 'date',
    title: 'Date & time',
    items: [
      { type: 'datePicker', label: 'Date', icon: Calendar },
      { type: 'time', label: 'Time', icon: Clock },
      { type: 'appointment', label: 'Appointment', icon: CalendarClock },
    ],
  },
  {
    id: 'layout',
    title: 'Layout',
    items: [
      { type: 'header', label: 'Heading', icon: Heading },
      { type: 'paragraph', label: 'Paragraph', icon: Pilcrow },
      { type: 'richText', label: 'Rich text', icon: FileText, description: 'Formatted content' },
      { type: 'divider', label: 'Divider', icon: Minus },
      { type: 'pageBreak', label: 'Page break', icon: GripVertical },
    ],
  },
  {
    id: 'media',
    title: 'Media & files',
    items: [
      { type: 'signature', label: 'Signature', icon: PenLine },
      { type: 'fileUpload', label: 'File upload', icon: Paperclip },
    ],
  },
  {
    id: 'end',
    title: 'Submit & pay',
    items: [
      {
        type: 'payment',
        label: 'Payment (Square)',
        icon: CreditCard,
        description: 'Card payments; link from appointment reschedule fee',
      },
      { type: 'submit', label: 'Submit', icon: Send },
    ],
  },
];

export const PALETTE_ID_PREFIX = '__palette__:' as const;

export function paletteDragId(type: FormFieldTypeId) {
  return `${PALETTE_ID_PREFIX}${type}`;
}

export function isPaletteDragId(id: string | number) {
  return String(id).startsWith(PALETTE_ID_PREFIX);
}

export function fieldTypeFromPaletteId(id: string | number): FormFieldTypeId | null {
  const s = String(id);
  if (!isPaletteDragId(s)) return null;
  return s.slice(PALETTE_ID_PREFIX.length) as FormFieldTypeId;
}
